export const CREATE_NEW = '$NEW$';

export type ChoiceItem =
  | boolean
  | number
  | ListChoiceItem
  | ListChoiceItem[]
  | ManualChoiceItem[]
  | Record<string, string>;

export type SomeChoice =
  | ListChoice
  | ManualChoice
  | NumberChoice
  | BooleanChoice
  | MultiSelectChoice
  | MappingChoice;

export type Choices = Record<string, SomeChoice>;

export type ChoicesMetadata = {
  version: number;
  choices: Choices;
};

export type Preferences = Record<
  string,
  Record<string, string> | ListChoiceItem | ManualChoiceItem[] | boolean | number
>;

export interface JsonObject {
  [key: string]: JsonObject | string | JsonObject[] | string[];
}

export type ManualChoiceItem = {
  type: string;
  label?: string;
  value?: string;
  separator?: string;
};

export type ManualChoice = Choice<ManualChoiceItem[]> & {
  type: 'manual';
  fromField?: string;
  /**
   * This is used when we only want to use freetext as an option
   */
  onlyPrefix?: boolean;
};

export type ListChoiceItem = {
  id: string;
  label: string;
  columns?: Record<string, any>;
};

/**
 * create: whether or not the user can create a new option
 */
type ListChoiceMetadata = {
  create: boolean;
  choices: ListChoiceItem[];
};

export type RowEntryDatatype = 'anchor' | 'string' | 'date';

export type RowEntryAnchorDatatype = {
  href: string;
  text: string;
};

export type TableChoiceColumn = {
  id: string;
  title: string;
  datatype: RowEntryDatatype;
};

export type TableChoiceView = {
  type: 'table';
  data: TableChoiceColumn[];
};

export type DropdownChoiceView = {
  type: 'dropdown';
};

export type ListChoiceView = TableChoiceView | DropdownChoiceView;

export type ListChoice = Choice<ListChoiceItem> &
  ListChoiceMetadata & {
    type: 'list';
    view?: ListChoiceView;
    /**
     * This is used in the frontend for:
     * A list choice with 1 choice, where you cannot create a new one, and a default is chosen, should not be shown to the user
     * Except when the alwaysShow boolean is true
     * */
    alwaysShow?: boolean;
  };

export type BooleanChoice = Choice<boolean> & {
  type: 'boolean';
};

export type NumberChoice = Choice<number> & { type: 'number' };

export type ChoiceType = 'list' | 'manual' | 'boolean' | 'multiselect' | 'mapping' | 'number';

export type Choice<CHOICE_TYPE extends ChoiceItem> = {
  isPrerequisite?: boolean;
  type: ChoiceType;
  label: string;
  required: boolean;
  default?: CHOICE_TYPE;
  infoText?: string;
  relatedTo: 'invoice' | 'deal' | 'contact' | 'order' | 'quotation';
  dependencies?: Record<string, Choices>;
};

export type MultiSelectChoice = Choice<ListChoiceItem[]> &
  ListChoiceMetadata & { type: 'multiselect' };

export type MappingChoice = Choice<Record<string, string>> & {
  type: 'mapping';
  choices: {
    to: ListChoiceItem[];
    from: ListChoiceItem[];
  };
  fromField: string;
};
